import PropTypes from 'prop-types';
import { DetailedIconThumbsUp } from '@zillow/constellation';
import { Box, Button, ButtonGroup, Heading, Modal, Paragraph } from '@zillow/constellation-10';

import ModalFooter from '../../../Modal/ModalFooter';

import StringConstants from '../../../../constants/StringConstants';
import numberUtils from '../../../../utils/numberUtils';
import PropertyConstants from '../../../../constants/PropertyConstants';
import { REQUIRE_SECURITY_DEPOSIT, WAIVE_SECURITY_DEPOSIT } from '../constants';
const { SYNDICATION_OVERVIEW_STATUS } = PropertyConstants;

const propTypes = {
  isForManagePolicyPageV2: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  securityDepositValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  syndication: PropTypes.object.isRequired,
  securityDepositSelection: PropTypes.oneOf([REQUIRE_SECURITY_DEPOSIT, WAIVE_SECURITY_DEPOSIT]),
};
const defaultProps = {};

const syndicationByBrand = [
  StringConstants.UNFORMATTED_BRAND_NAMES.zillow,
  StringConstants.UNFORMATTED_BRAND_NAMES.trulia,
  StringConstants.UNFORMATTED_BRAND_NAMES.hotpads,
];

const ReviewSecurityDepositConfirmationModal = ({
  isForManagePolicyPageV2,
  onClose,
  securityDepositValue,
  syndication,
  securityDepositSelection,
  onSecurityDepositSubmitSuccess,
}) => {
  const {
    overview: { status: syndicationStatus },
  } = syndication;

  const shouldRenderViewOnNetworkButtons =
    syndicationStatus !== SYNDICATION_OVERVIEW_STATUS.INACTIVE ||
    syndicationStatus !== SYNDICATION_OVERVIEW_STATUS.DECLINED;

  const syndicationBrandContainsUrl = syndicationByBrand.some((brand) => {
    return syndication?.[brand]?.url;
  });

  const isSecurityDepositRequired = securityDepositSelection === REQUIRE_SECURITY_DEPOSIT;

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={
        <Heading level={3}>
          {isSecurityDepositRequired ? 'Security deposit confirmed' : 'Security deposit updated'}
        </Heading>
      }
      footer={<ModalFooter submitText="OK" onSubmit={onSecurityDepositSubmitSuccess} />}
      size={{ base: 'fullScreen', md: 'lg' }}
      body={
        <div data-testid="rental-protection-two-step-opt-in-review-security-deposit-confirmation-modal">
          <Box css={{ paddingBlock: { base: '200', md: '800' }, paddingInline: { base: '200', md: '600' } }}>
            <Box css={{ display: 'flex', flexDirection: 'column', marginBottom: '200' }}>
              <DetailedIconThumbsUp size="lg" marginBottom="sm" />
              <Heading level={4} fontFamily="heading-alt" css={{ marginBottom: '400' }}>
                {isSecurityDepositRequired
                  ? 'You&apos;re requiring a security deposit'
                  : 'Great choice, your listing is now updated'}
              </Heading>
              <Paragraph textStyle="body-bold">
                {isSecurityDepositRequired ? 'New security' : 'Security'} deposit amount
              </Paragraph>
              <Paragraph css={{ marginBottom: isForManagePolicyPageV2 || isSecurityDepositRequired ? '400' : '0' }}>
                {numberUtils.asCurrency(securityDepositValue, '$0,0')}
              </Paragraph>
              {isSecurityDepositRequired && (
                <Paragraph css={{ marginBottom: isForManagePolicyPageV2 ? '400' : '0' }}>
                  If you change your mind, you can always edit your listing later.
                </Paragraph>
              )}
              {isForManagePolicyPageV2 && (
                <>
                  <Paragraph textStyle="body-bold">Next steps</Paragraph>
                  <Paragraph>
                    Sit back and wait for applications! Once you find a great fit, you&apos;ll review and verify their
                    income documents.
                  </Paragraph>
                </>
              )}

              {shouldRenderViewOnNetworkButtons && (
                <ButtonGroup
                  aria-label="view property buttons"
                  css={{ marginTop: syndicationBrandContainsUrl ? '800' : '0' }}
                  orientation={{ base: 'vertical', lg: 'horizontal' }}
                >
                  {syndicationByBrand.map((brand) => {
                    const displayBrand = StringConstants.FORMATTED_BRAND_NAMES[brand];

                    if (syndication?.[brand]?.url) {
                      return (
                        <Button
                          key={`review-security-deposit-confirmation-modal-syndication-btn-${brand}`}
                          asChild
                          target="_blank"
                          tone="brand"
                          emphasis="filled"
                        >
                          <a href={syndication?.[brand]?.url}>{`View on ${displayBrand}`}</a>
                        </Button>
                      );
                    }
                  })}
                </ButtonGroup>
              )}
            </Box>
          </Box>
        </div>
      }
    />
  );
};

ReviewSecurityDepositConfirmationModal.propTypes = propTypes;
ReviewSecurityDepositConfirmationModal.defaultProps = defaultProps;

export default ReviewSecurityDepositConfirmationModal;
